import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import {
  TrendingUp,
  BookOpen,
  Users,
  Globe,
  ArrowRight,
  Play,
  CheckCircle,
  Calendar,
  Lock,
  LogOut,
  Mail,
  Loader2,
  Sparkles,
  User,
  Settings,
  ChevronDown,
  LayoutDashboard,
} from "lucide-react";
import { toast } from "sonner";
import AuthModal from "@/components/AuthModal";

import WealthsimpleStepsModal from "@/components/WealthsimpleStepsModal";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Facts carousel data - rotating every 10 seconds
const INVESTING_FACTS = [
  { stat: "7,000+", text: "International students arrive in Canada each year with no investing knowledge" },
  { stat: "73%", text: "of Canadians aged 18-34 say they wish they started investing earlier" },
  { stat: "$7,000", text: "Annual TFSA contribution room - tax-free growth for life" },
  { stat: "10.5%", text: "Average annual return of the S&P 500 over the past 30 years" },
  { stat: "$1M+", text: "What $200/month could become in 40 years at 8% returns" },
  { stat: "68%", text: "of young Canadians don't know the difference between TFSA and RRSP" },
  { stat: "5 years", text: "Average time international students wait before opening an investment account" },
  { stat: "$47,000", text: "Average student loan debt in Canada - proper budgeting is essential" },
  { stat: "2x", text: "Your money can double every 9 years with 8% returns (Rule of 72)" },
  { stat: "30%", text: "of Canadians have zero retirement savings - don't be one of them" },
];

const Home = () => {
  const [authOpen, setAuthOpen] = useState(false);
  const [wealthsimpleModalOpen, setWealthsimpleModalOpen] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [seminars, setSeminars] = useState([]);
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);

  // Rotating facts carousel - every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFactIndex((prev) => (prev + 1) % INVESTING_FACTS.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Check if user is logged in
  useEffect(() => {
    // Check localStorage first (remember me), then sessionStorage
    let token = localStorage.getItem("user_token");
    let userData = localStorage.getItem("user_data");
    
    // If not in localStorage, check sessionStorage
    if (!token || !userData) {
      token = sessionStorage.getItem("user_token");
      userData = sessionStorage.getItem("user_data");
    }
    
    if (token && userData) {
      setUser(JSON.parse(userData));
      setIsLoggedIn(true);
    }
    
    // Check if user has completed onboarding (both quizzes)
    const onboardingComplete = localStorage.getItem("onboardingComplete");
    const recommendedCategory = localStorage.getItem("recommendedCategory");
    if (onboardingComplete === "true" && recommendedCategory) {
      setHasCompletedOnboarding(true);
    }
  }, []);

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    // Clear from both storage types
    localStorage.removeItem("user_token");
    localStorage.removeItem("user_data");
    localStorage.removeItem("remember_me");
    sessionStorage.removeItem("user_token");
    sessionStorage.removeItem("user_data");
    setUser(null);
    setIsLoggedIn(false);
    toast.success("Logged out successfully");
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [postsRes, seminarsRes] = await Promise.all([
          axios.get(`${API_URL}/api/blog`),
          axios.get(`${API_URL}/api/seminars`),
        ]);
        setBlogPosts(postsRes.data.slice(0, 3));
        setSeminars(seminarsRes.data.slice(0, 2));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const features = [
    {
      icon: BookOpen,
      title: "Learn the Basics",
      description:
        "TFSA, RRSP, ETFs explained simply. No jargon, just clarity.",
    },
    {
      icon: Users,
      title: "Built for Students & Young Adults",
      description:
        "Strategies designed for irregular income and busy schedules.",
    },
    {
      icon: Globe,
      title: "New to Canada?",
      description:
        "International student-friendly guides. Start from zero.",
    },
    {
      icon: TrendingUp,
      title: "Long-term Habits",
      description:
        "Focus on consistency, not timing the market.",
    },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* ========== MOBILE VERSION ========== */}
      <div className="md:hidden">
        {/* Mobile Hero - Clean and Simple */}
        <section className="gradient-mesh py-8 px-4">
          <div className="text-center">
            <Badge className="bg-emerald-100 text-emerald-700 mb-3 hover:bg-emerald-100">
              🇨🇦 For Canadian Students
            </Badge>
            <h1 className="text-2xl font-heading font-bold text-navy-700 mb-3 leading-tight">
              Start Investing in Canada
              <span className="text-emerald-500"> Without the Hype</span>
            </h1>
            <p className="text-sm text-slate-600 mb-6 leading-relaxed">
              Clear, beginner-friendly education for Canadian young adults and international
              students. Build confidence, start small, and grow your wealth
              over time. No get-rich-quick schemes, just real knowledge.
            </p>

            {/* Sign In / Welcome */}
            {!isLoggedIn ? (
              <Button
                onClick={() => setAuthOpen(true)}
                className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white text-base font-semibold rounded-xl mb-6"
                data-testid="mobile-signup-btn"
              >
                Get Started for Free!
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            ) : (
              <div className="text-center mb-6">
                <p className="text-emerald-600 font-medium text-sm">
                  ✓ Welcome, {user?.email?.split('@')[0]}!
                </p>
              </div>
            )}

            {/* Investor Quiz Cards - Mobile */}
            <div className="space-y-3 mb-6">
              {isLoggedIn ? (
                <>
                  <Link to="/onboarding" className="block">
                    <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl p-4 text-white text-left">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">🎯</span>
                        <div>
                          <h3 className="font-bold text-base">Investor Type Quiz</h3>
                          <p className="text-emerald-100 text-xs">3-min personality quiz</p>
                        </div>
                        <ArrowRight className="w-5 h-5 ml-auto" />
                      </div>
                    </div>
                  </Link>
                  <Link to="/category-quiz" className="block">
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-4 text-white text-left">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">🚀</span>
                        <div>
                          <h3 className="font-bold text-base">Category Recommendation</h3>
                          <p className="text-purple-100 text-xs">Find your starting point</p>
                        </div>
                        <ArrowRight className="w-5 h-5 ml-auto" />
                      </div>
                    </div>
                  </Link>
                  
                  {/* Go to Dashboard link - only shown after completing both quizzes */}
                  {hasCompletedOnboarding && (
                    <Link to="/dashboard" className="block">
                      <div className="bg-gradient-to-r from-navy-700 to-navy-800 rounded-xl p-4 text-white text-left">
                        <div className="flex items-center gap-3">
                          <LayoutDashboard className="w-6 h-6" />
                          <div>
                            <h3 className="font-bold text-base">Go to Your Dashboard</h3>
                            <p className="text-slate-300 text-xs">Your personalized journey awaits</p>
                          </div>
                          <ArrowRight className="w-5 h-5 ml-auto" />
                        </div>
                      </div>
                    </Link>
                  )}
                </>
              ) : (
                <>
                  <div onClick={() => setAuthOpen(true)} className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl p-4 text-white text-left opacity-80 cursor-pointer">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🎯</span>
                      <div>
                        <h3 className="font-bold text-base">Investor Type Quiz</h3>
                        <p className="text-emerald-100 text-xs">🔒 Sign up to unlock</p>
                      </div>
                    </div>
                  </div>
                  <div onClick={() => setAuthOpen(true)} className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-4 text-white text-left opacity-80 cursor-pointer">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🚀</span>
                      <div>
                        <h3 className="font-bold text-base">Ready to Invest?</h3>
                        <p className="text-purple-100 text-xs">🔒 Sign up to unlock</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Mobile Primary Goal Categories */}
        <section className="py-6 px-4 bg-white">
          <h2 className="text-lg font-heading font-bold text-navy-700 mb-4 text-center">
            Choose Your Goal
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <Link to="/start-investing" className="block">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center hover:bg-emerald-100 transition-colors">
                <span className="text-2xl mb-2 block">📈</span>
                <h3 className="font-semibold text-sm text-navy-700">Start Investing</h3>
              </div>
            </Link>
            <Link to="/debt-free" className="block">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center hover:bg-blue-100 transition-colors">
                <span className="text-2xl mb-2 block">💳</span>
                <h3 className="font-semibold text-sm text-navy-700">Get Debt-Free</h3>
              </div>
            </Link>
            <Link to="/build-wealth" className="block">
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center hover:bg-purple-100 transition-colors">
                <span className="text-2xl mb-2 block">💰</span>
                <h3 className="font-semibold text-sm text-navy-700">Build Wealth</h3>
              </div>
            </Link>
            <Link to="/plan-save" className="block">
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center hover:bg-orange-100 transition-colors">
                <span className="text-2xl mb-2 block">🎯</span>
                <h3 className="font-semibold text-sm text-navy-700">Plan & Save</h3>
              </div>
            </Link>
            <Link to="/learn-finance" className="block col-span-2">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center hover:bg-slate-100 transition-colors">
                <span className="text-2xl mb-2 block">📚</span>
                <h3 className="font-semibold text-sm text-navy-700">Learn Finance</h3>
              </div>
            </Link>
          </div>
        </section>

        {/* Mobile Wealthsimple Banner */}
        {isLoggedIn && (
          <section className="py-4 px-4 bg-[#00C853]">
            <div 
              onClick={() => setWealthsimpleModalOpen(true)}
              className="flex items-center justify-between text-white cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                  <span className="text-[#00C853] text-sm font-bold">W</span>
                </div>
                <span className="font-bold text-sm">Get free $25 towards your Wealthsimple TFSA</span>
              </div>
              <Button size="sm" className="bg-white text-[#00C853] hover:bg-white/90 text-sm px-4 rounded-full font-bold">
                Claim →
              </Button>
            </div>
          </section>
        )}

        {/* Mobile Quick Links */}
        {isLoggedIn && (
          <section className="py-6 px-4 bg-slate-50">
            <h2 className="text-lg font-heading font-bold text-navy-700 mb-4">Quick Access</h2>
            <div className="space-y-2">
              <Link to="/tools?tool=simulator" className="flex items-center gap-3 bg-white rounded-xl p-3 shadow-sm">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-navy-700">My Portfolio</h3>
                  <p className="text-xs text-slate-500">Practice trading</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 ml-auto" />
              </Link>
              <Link to="/tools" className="flex items-center gap-3 bg-white rounded-xl p-3 shadow-sm">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-navy-700">All Tools</h3>
                  <p className="text-xs text-slate-500">Calculators & quizzes</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 ml-auto" />
              </Link>
              <Link to="/blog" className="flex items-center gap-3 bg-white rounded-xl p-3 shadow-sm">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-navy-700">Blog & Courses</h3>
                  <p className="text-xs text-slate-500">Learn with videos</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 ml-auto" />
              </Link>
            </div>
          </section>
        )}
      </div>

      {/* ========== DESKTOP VERSION ========== */}
      <div className="hidden md:block">
      {/* Top Bar - "For Canadians" + User Profile */}
      <div className="bg-navy-700 text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg">🇨🇦</span>
            <span className="font-medium text-sm">For Canadians</span>
          </div>
          
          {/* User Profile Circle */}
          {isLoggedIn && user ? (
            <div className="relative">
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex items-center gap-2 hover:bg-white/10 rounded-full px-3 py-1 transition-colors"
                data-testid="profile-menu-btn"
              >
                <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {user.email?.charAt(0).toUpperCase()}
                </div>
                <ChevronDown className={`w-4 h-4 transition-transform ${showProfileMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {showProfileMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg py-2 z-50"
                >
                  <div className="px-4 py-2 border-b border-slate-100">
                    <p className="text-sm text-slate-500">Signed in as</p>
                    <p className="text-sm font-medium text-slate-800 truncate">{user.email}</p>
                  </div>
                  <Link
                    to="/?tool=simulator"
                    className="flex items-center gap-3 px-4 py-2 text-slate-700 hover:bg-slate-50 transition-colors"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    <TrendingUp className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm">My Portfolio</span>
                  </Link>
                  <Link
                    to="/"
                    className="flex items-center gap-3 px-4 py-2 text-slate-700 hover:bg-slate-50 transition-colors"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    <Settings className="w-4 h-4 text-slate-500" />
                    <span className="text-sm">Tools & Quizzes</span>
                  </Link>
                  <button
                    onClick={() => { handleLogout(); setShowProfileMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="text-sm">Sign Out</span>
                  </button>
                </motion.div>
              )}
            </div>
          ) : (
            <button
              onClick={() => setAuthOpen(true)}
              className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 rounded-full px-4 py-1.5 text-sm font-medium transition-colors"
            >
              <User className="w-4 h-4" />
              Sign In
            </button>
          )}
        </div>
      </div>

      {/* Hero Section - Desktop Only */}
      <div className="hidden md:block">
        <section className="relative gradient-mesh py-20 lg:py-32 overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
              <Badge className="bg-emerald-100 text-emerald-700 mb-4 hover:bg-emerald-100">
                For Canadian Students
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-navy-700 mb-6 leading-tight">
                Start Investing in Canada
                <span className="text-emerald-500"> Without the Hype</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed max-w-2xl mx-auto">
                Clear, beginner-friendly education for Canadian young adults and international
                students. Build confidence, start small, and grow your wealth
                over time. No get-rich-quick schemes, just real knowledge.
              </p>

              {/* Auth Section - Gate to unlock content */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mb-10 p-8 bg-gradient-to-br from-navy-700 to-navy-800 rounded-3xl shadow-floating max-w-2xl mx-auto"
              >
                {isLoggedIn ? (
                  <div className="text-center">
                    <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-heading font-bold text-white mb-2">
                      Welcome back, {user?.email?.split('@')[0]}! 🎉
                    </h3>
                    <p className="text-slate-300 mb-4">
                      All tools and quizzes are unlocked. Let&apos;s build your wealth!
                    </p>
                    <Button
                      onClick={handleLogout}
                      variant="ghost"
                      className="text-slate-400 hover:text-white hover:bg-white/10"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </Button>
                  </div>
                ) : (
                  <div className="text-center">
                    <span className="text-4xl mb-2 block">🔓</span>
                    <h3 className="text-2xl font-heading font-bold text-white mb-2">
                      Create Your Free Account
                    </h3>
                    <p className="text-slate-300 text-sm mb-6">
                      Sign up to unlock all quizzes, calculators, and save your personalized results!
                    </p>
                    <Button
                      onClick={() => setAuthOpen(true)}
                      className="w-full sm:w-auto h-12 bg-emerald-500 hover:bg-emerald-600 text-white text-lg font-semibold rounded-xl px-8"
                      data-testid="hero-signup-btn"
                    >
                      Get Started for Free!
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                    <p className="text-center text-xs text-slate-500 mt-4">
                      🔒 Your data is safe. We never share your information.
                    </p>
                  </div>
                )}
              </motion.div>

              {/* Two Surveys Side by Side - Only show after logged in */}
              {isLoggedIn && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-8"
                >
                  <p className="text-center text-slate-600 mb-6 font-medium">
                    👇 Take these quizzes to get your personalized plan!
                  </p>
                  <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
                    {/* Survey 1: Investor Type Quiz */}
                    <Link to="/onboarding" className="block">
                      <motion.div
                        whileHover={{ scale: 1.03, y: -5 }}
                        whileTap={{ scale: 0.98 }}
                        className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white text-left h-full cursor-pointer shadow-lg"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-3xl">🎯</span>
                          <Badge className="bg-white/20 text-white border-0">Step 1</Badge>
                        </div>
                        <h3 className="text-xl font-heading font-bold mb-2">
                          What Kind of Investor Are You?
                        </h3>
                        <p className="text-emerald-100 text-sm mb-4">
                          Take our fun 3-min quiz to discover your investing personality!
                        </p>
                        <div className="flex items-center text-white font-medium">
                          Start Quiz <ArrowRight className="ml-2 w-4 h-4" />
                        </div>
                      </motion.div>
                    </Link>

                    {/* Survey 2: Category Recommendation */}
                    <Link to="/category-quiz" className="block">
                      <motion.div
                        whileHover={{ scale: 1.03, y: -5 }}
                        whileTap={{ scale: 0.98 }}
                        className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white text-left h-full cursor-pointer shadow-lg"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-3xl">🚀</span>
                          <Badge className="bg-white/20 text-white border-0">Step 2</Badge>
                        </div>
                        <h3 className="text-xl font-heading font-bold mb-2">
                          Find Your Starting Point
                        </h3>
                        <p className="text-purple-100 text-sm mb-4">
                          Answer quick questions to get your personalized category!
                        </p>
                        <div className="flex items-center text-white font-medium">
                          Get My Plan <ArrowRight className="ml-2 w-4 h-4" />
                        </div>
                      </motion.div>
                    </Link>
                  </div>

                  {/* Go to Dashboard - only shown after completing both quizzes */}
                  {hasCompletedOnboarding && (
                    <div className="mt-6 max-w-3xl mx-auto">
                      <Link to="/dashboard">
                        <motion.div
                          whileHover={{ scale: 1.02, y: -3 }}
                          whileTap={{ scale: 0.98 }}
                          className="bg-gradient-to-r from-navy-700 to-navy-800 rounded-2xl p-5 text-white cursor-pointer shadow-lg flex items-center justify-between"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center">
                              <LayoutDashboard className="w-6 h-6" />
                            </div>
                            <div>
                              <h3 className="text-lg font-heading font-bold">Go to Your Dashboard</h3>
                              <p className="text-slate-300 text-sm">Your personalized journey awaits</p>
                            </div>
                          </div>
                          <ArrowRight className="w-5 h-5" />
                        </motion.div>
                      </Link>
                    </div>
                  )}

                  {/* Fun message */}
                  <p className="text-slate-500 text-sm mt-6 text-center">
                    ✨ <span className="font-medium">Pro tip:</span> Take Quiz 1 first to discover your type, then Quiz 2 to find your starting point!
                  </p>
                  
                  {/* Quick Access to Portfolio */}
                  <div className="mt-8 max-w-3xl mx-auto">
                    <Link to="/tools?tool=simulator">
                      <motion.div
                        whileHover={{ scale: 1.02, y: -3 }}
                        whileTap={{ scale: 0.98 }}
                        className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl p-6 text-white cursor-pointer shadow-lg flex items-center justify-between"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                            <TrendingUp className="w-7 h-7" />
                          </div>
                          <div>
                            <h3 className="text-xl font-heading font-bold">Go to My Portfolio</h3>
                            <p className="text-blue-100 text-sm">Practice trading with $10,000 CAD virtual cash</p>
                          </div>
                        </div>
                        <ArrowRight className="w-6 h-6" />
                      </motion.div>
                    </Link>
                  </div>
                </motion.div>
              )}

              {/* Preview cards when not logged in */}
              {!isLoggedIn && (
                <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto mb-8">
                  <div 
                    onClick={() => setAuthOpen(true)}
                    className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white text-left relative overflow-hidden opacity-80 cursor-pointer hover:opacity-90 transition-opacity"
                  >
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10">
                      <div className="bg-white text-navy-700 px-4 py-2 rounded-full font-semibold text-sm shadow-lg">
                        🔒 Sign up to unlock
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-3 mt-8">
                      <span className="text-3xl">🎯</span>
                      <Badge className="bg-white/20 text-white border-0">Step 1</Badge>
                    </div>
                    <h3 className="text-xl font-heading font-bold mb-2">
                      What Kind of Investor Are You?
                    </h3>
                    <p className="text-emerald-100 text-sm">3-min personality quiz</p>
                  </div>
                  <div 
                    onClick={() => setAuthOpen(true)}
                    className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white text-left relative overflow-hidden opacity-80 cursor-pointer hover:opacity-90 transition-opacity"
                  >
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10">
                      <div className="bg-white text-navy-700 px-4 py-2 rounded-full font-semibold text-sm shadow-lg">
                        🔒 Sign up to unlock
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-3 mt-8">
                      <span className="text-3xl">🚀</span>
                      <Badge className="bg-white/20 text-white border-0">Step 2</Badge>
                    </div>
                    <h3 className="text-xl font-heading font-bold mb-2">
                      Ready to Start Investing?
                    </h3>
                    <p className="text-purple-100 text-sm">5-question action plan</p>
                  </div>
                </div>
              )}
            </motion.div>
        </div>
      </section>
      </div>

      {/* Wealthsimple Banner - Desktop only */}
      <section className={`hidden md:block py-8 ${isLoggedIn ? 'bg-[#00C853]' : 'bg-slate-400'} transition-colors`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isLoggedIn ? (
            <div 
              onClick={() => setWealthsimpleModalOpen(true)}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 text-white hover:opacity-90 transition-opacity cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  <span className="text-[#00C853] text-xl font-bold">W</span>
                </div>
                <span className="font-heading font-bold text-lg">
                  Get free $25 towards your Wealthsimple TFSA account
                </span>
              </div>
              <Button className="bg-white text-[#00C853] hover:bg-white/90 text-lg px-8 py-6 rounded-full font-bold shadow-lg">
                Claim Free $25 →
              </Button>
            </div>
          ) : (
            <div 
              onClick={() => setAuthOpen(true)}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 text-white cursor-pointer hover:opacity-90 transition-opacity"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/50 rounded-lg flex items-center justify-center">
                  <span className="text-slate-500 text-xl font-bold">W</span>
                </div>
                <span className="font-heading font-bold text-lg">
                  Get free $25 towards your Wealthsimple TFSA account
                </span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 text-white text-lg px-8 py-4 rounded-full font-bold">
                <Lock className="w-5 h-5" />
                <span>Sign up to unlock</span>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Features Section - Desktop only */}
      <section className="hidden md:block py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-navy-700 mb-4">
              What You&apos;ll Learn
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to start investing confidently as a student.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full card-hover border-0 shadow-card bg-slate-50/50">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-emerald-600" />
                    </div>
                    <h3 className="font-heading font-bold text-lg text-navy-700 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-slate-600 text-sm">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section - Desktop only */}
      <section className="hidden md:block py-16 bg-gradient-to-br from-emerald-500 to-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-5">
            <Mail className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-3">
            Join Our Newsletter
          </h2>
          <p className="text-emerald-100 mb-2 max-w-xl mx-auto">
            Be the first to know when we host sessions, launch courses, and release more amazing financial tools.
          </p>
          <p className="text-white font-medium text-sm mb-6">
            <Sparkles className="w-4 h-4 inline mr-1" />
            Newsletter subscribers get exclusive early-bird discounts!
          </p>
          
          {newsletterSuccess ? (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white/20 rounded-2xl p-5 max-w-sm mx-auto"
            >
              <CheckCircle className="w-10 h-10 text-white mx-auto mb-2" />
              <p className="text-white font-medium">You&apos;re on the list!</p>
              <p className="text-emerald-100 text-sm">We&apos;ll notify you about new blogs, market updates, content, and AI tools!</p>
            </motion.div>
          ) : (
            <form 
              onSubmit={async (e) => {
                e.preventDefault();
                if (!newsletterEmail) {
                  toast.error("Please enter your email");
                  return;
                }
                setNewsletterLoading(true);
                try {
                  await axios.post(`${API_URL}/api/subscribers`, {
                    email: newsletterEmail,
                    phone: "",
                    source: "newsletter_home",
                  });
                  setNewsletterSuccess(true);
                  setNewsletterEmail("");
                  toast.success("Welcome to the Compoundr newsletter! 🎉");
                } catch (error) {
                  if (error.response?.status === 400) {
                    toast.success("You&apos;re already subscribed!");
                    setNewsletterSuccess(true);
                  } else {
                    toast.error("Something went wrong. Please try again.");
                  }
                } finally {
                  setNewsletterLoading(false);
                }
              }} 
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <Input
                type="email"
                placeholder="Enter your email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="flex-1 h-12 bg-white/90 border-0 text-slate-800 placeholder:text-slate-400"
                data-testid="home-newsletter-email"
              />
              <Button
                type="submit"
                disabled={newsletterLoading}
                className="h-12 bg-white text-emerald-600 hover:bg-emerald-50 px-8 font-semibold"
                data-testid="home-newsletter-submit"
              >
                {newsletterLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Subscribe
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          )}
          
          <p className="text-emerald-200 text-xs mt-4">
            No spam, ever. Unsubscribe anytime.
          </p>
        </div>
      </section>

      {/* Why We're Different - Desktop only */}
      <section className="hidden md:block py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-navy-700 mb-6">
                New to Canada? We&apos;ve Got You.
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                International students face unique challenges. Different banking
                system, unfamiliar tax rules, and no family nearby to ask for
                advice. We break down everything step-by-step.
              </p>
              <ul className="space-y-4">
                {[
                  "How to get a SIN and open investment accounts",
                  "Understanding TFSA vs RRSP (and why TFSA is your friend)",
                  "Building Canadian credit while you study",
                  "Currency exchange tips for receiving money from home",
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1750793429382-033404063213?w=600&q=80"
                alt="Canadian city skyline"
                className="rounded-2xl shadow-card"
              />
              {/* Rotating Facts Carousel */}
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-floating min-w-[220px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentFactIndex}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <p className="font-mono text-2xl font-bold text-emerald-600">
                      {INVESTING_FACTS[currentFactIndex].stat}
                    </p>
                    <p className="text-sm text-slate-500">
                      {INVESTING_FACTS[currentFactIndex].text}
                    </p>
                  </motion.div>
                </AnimatePresence>
                {/* Progress dots */}
                <div className="flex gap-1 mt-3 justify-center">
                  {INVESTING_FACTS.slice(0, 5).map((_, idx) => (
                    <div
                      key={idx}
                      className={`w-1.5 h-1.5 rounded-full transition-colors ${
                        idx === currentFactIndex % 5 ? 'bg-emerald-500' : 'bg-slate-200'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Articles */}
      {blogPosts.length > 0 && (
        <section className="py-20 lg:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-12">
              <div>
                <h2 className="text-3xl font-heading font-bold text-navy-700 mb-2">
                  Latest Articles
                </h2>
                <p className="text-slate-600">Fresh insights for student investors</p>
              </div>
              <Link to="/blog">
                <Button variant="outline" className="hidden sm:flex">
                  View All
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {blogPosts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/blog/${post.slug}`}>
                    <Card className="h-full card-hover border-0 shadow-card overflow-hidden">
                      {post.image_url && (
                        <img
                          src={post.image_url}
                          alt={post.title}
                          className="w-full h-48 object-cover"
                        />
                      )}
                      <CardContent className="pt-4">
                        <Badge variant="secondary" className="mb-2">
                          {post.category}
                        </Badge>
                        <h3 className="font-heading font-bold text-lg text-navy-700 mb-2 line-clamp-2">
                          {post.title}
                        </h3>
                        <p className="text-slate-600 text-sm line-clamp-2">
                          {post.excerpt}
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Upcoming Seminars */}
      {seminars.length > 0 && (
        <section className="py-20 lg:py-32 bg-navy-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
                Free Live Seminars
              </h2>
              <p className="text-slate-300 text-lg">
                Join our free online workshops and learn with other students
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {seminars.map((seminar) => (
                <Card
                  key={seminar.id}
                  className="bg-white/10 backdrop-blur border-white/20"
                >
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      <div className="bg-emerald-500 text-white rounded-lg p-3 text-center min-w-[70px]">
                        <Calendar className="w-5 h-5 mx-auto mb-1" />
                        <p className="text-xs">
                          {new Date(seminar.date).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })}
                        </p>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading font-bold text-white mb-1">
                          {seminar.title}
                        </h3>
                        <p className="text-slate-300 text-sm mb-2">
                          {seminar.time} • {seminar.speaker}
                        </p>
                        <p className="text-xs text-slate-400">
                          {seminar.spots_available - seminar.spots_taken} spots remaining
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center mt-8">
              <Link to="/seminars">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8">
                  View All Seminars
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section - Desktop only */}
      <section className="hidden md:block py-20 lg:py-32 bg-gradient-to-br from-emerald-500 to-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-lg text-emerald-100 mb-8 max-w-2xl mx-auto">
            Join thousands of Canadian students who are building their financial
            future. Get free weekly tips, exclusive seminar invites, and a
            supportive community.
          </p>
          {isLoggedIn ? (
            <Link to="/courses">
              <Button
                className="bg-white text-emerald-600 hover:bg-emerald-50 rounded-full px-10 py-6 text-lg btn-hover-scale"
                data-testid="cta-signup-btn"
              >
                Explore Tools
              </Button>
            </Link>
          ) : (
            <Button
              onClick={() => setAuthOpen(true)}
              className="bg-white text-emerald-600 hover:bg-emerald-50 rounded-full px-10 py-6 text-lg btn-hover-scale"
              data-testid="cta-signup-btn"
            >
              Join Compoundr for Free
            </Button>
          )}
        </div>
      </section>
      </div>
      {/* End Desktop Version */}

      <AuthModal 
        isOpen={authOpen} 
        onClose={() => setAuthOpen(false)} 
        onSuccess={handleAuthSuccess}
        required={true}
      />
      <WealthsimpleStepsModal 
        open={wealthsimpleModalOpen} 
        onOpenChange={setWealthsimpleModalOpen} 
      />
      
    </div>
  );
};

export default Home;
